import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { api } from "../api";
import DatasetChatView from "../components/DatasetChatView";
import { useModal } from "../ui/modal";
import { katalog, useT } from "../i18n";
import { Button, StatusBadge as UiStatusBadge, statusLabel, EmptyState } from "../ui";

interface DatasetColumn {
  name: string;
  type: "text" | "number" | "date" | "bool" | "enum" | "list";
  description?: string;
}

// run_stats-Diagnose aus dem v2-Runner (dataset-extraction-v2-plan). Alle Keys
// optional — Alt-Datasets haben run_stats=null.
interface RunStats {
  chunk_errors?: number;
  enum_nulls?: number;
  coverage_misses?: number;
  followup_ran?: boolean;
  [k: string]: unknown;
}

interface DatasetOut {
  id: string;
  name: string;
  description: string | null;
  source_type: "documents" | "web";
  source_config: Record<string, unknown>;
  columns: DatasetColumn[];
  status: "draft" | "queued" | "extracting" | "ready" | "error";
  last_error: string | null;
  row_count: number;
  // v2: Coverage-Gate-Ergebnis + Runner-Diagnose. Nullable (Alt-Datasets).
  coverage_pct: number | null;
  run_stats: RunStats | null;
}

interface DatasetRowOut {
  id: string;
  data: Record<string, unknown>;
  source_ref: string | null;
}

interface Doc {
  id: string;
  title: string;
}

export default function DatasetsPage() {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [datasets, setDatasets] = useState<DatasetOut[]>([]);

  const loadList = async () => setDatasets(await api.get<DatasetOut[]>("/datasets"));
  useEffect(() => {
    loadList();
  }, []);

  return (
    <div className="h-full flex md:grid md:grid-cols-[260px_1fr]">
      <aside
        className={clsx(
          "bg-cream-100/60 dark:bg-ink-800/60 md:border-r border-cream-200 dark:border-ink-600 overflow-hidden flex-col",
          datasetId ? "hidden md:flex" : "flex w-full md:w-auto",
        )}
      >
        <div className="p-2 border-b border-cream-200 dark:border-ink-600">
          <Button onClick={() => navigate("/ds")} className="w-full">
            <span className="text-base leading-none">+</span> {t.datasets.neuesDataset}
          </Button>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-cream-200/60 dark:divide-ink-600/60">
          {datasets.map((d) => (
            <button
              key={d.id}
              onClick={() => navigate(`/ds/${d.id}`)}
              className={
                "w-full text-left px-3 py-2 text-sm transition-colors " +
                (d.id === datasetId
                  ? "bg-brand-50 dark:bg-brand-700/15 border-l-2 border-brand-500"
                  : "hover:bg-cream-50 dark:hover:bg-ink-700/40 border-l-2 border-transparent")
              }
            >
              <div className="font-medium truncate text-ink">{d.name}</div>
              <div className="text-xs text-ink-50/60 mt-0.5 flex flex-wrap items-center gap-x-1 gap-y-1">
                <span>
                  {d.source_type === "documents" ? t.datasets.dokumente : t.datasets.web} ·{" "}
                  {t.datasets.zeilen(d.row_count)}
                </span>
                <StatusBadge status={d.status} />
                <CoverageBadge pct={d.coverage_pct} />
              </div>
            </button>
          ))}
          {datasets.length === 0 && (
            <EmptyState
              className="py-8 px-4"
              title={t.datasets.keineTabellen}
              hint={t.datasets.keineTabellenHinweis}
              action={
                <Button variant="secondary" onClick={() => navigate("/ds")}>
                  {t.datasets.neueTabelle}
                </Button>
              }
            />
          )}
        </div>
      </aside>

      <section
        className={clsx(
          "bg-cream-50 dark:bg-ink-900 overflow-hidden flex-1 min-w-0",
          datasetId ? "flex flex-col" : "hidden md:block",
        )}
      >
        {datasetId ? (
          <DatasetDetail
            datasetId={datasetId}
            onChange={async () => {
              await loadList();
            }}
            onBack={() => navigate("/ds")}
          />
        ) : (
          <CreateWizard
            onCreated={async (id) => {
              await loadList();
              navigate(`/ds/${id}`);
            }}
          />
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const t = useT();
  const info = statusLabel(status);
  // Datasets haben eigene Zustands-Texte (z. B. „Wird extrahiert" statt
  // „Extrahiert …") — der Text kommt aus dem Namespace, die FARBE weiter aus
  // ui/labels.ts. Cast, weil hier ein roher Server-String indiziert.
  const label = (t.datasets.status as Record<string, string>)[status] ?? info.label;
  return <UiStatusBadge status={info.status}>{label}</UiStatusBadge>;
}

// Coverage-Gate-Badge: grün ≥95 %, gelb 90–95 %, rot <90 %. null → nichts
// (Alt-Datasets ohne Coverage-Messung).
function CoverageBadge({ pct }: { pct: number | null | undefined }) {
  const t = useT();
  if (pct == null) return null;
  const status = pct >= 0.95 ? "success" : pct >= 0.9 ? "warning" : "danger";
  return (
    <UiStatusBadge status={status}>{t.datasets.abdeckung(Math.round(pct * 100))}</UiStatusBadge>
  );
}

// run_stats dezent in lesbare Hinweise übersetzen (kein Alarm-Design).
// Keine Komponente → `katalog()` (docs/i18n.md §3).
function runStatsNotes(rs: RunStats | null | undefined): string[] {
  if (!rs) return [];
  const t = katalog().datasets;
  const notes: string[] = [];
  if (typeof rs.chunk_errors === "number" && rs.chunk_errors > 0) {
    notes.push(t.abschnitteUebersprungen(rs.chunk_errors));
  }
  if (typeof rs.enum_nulls === "number" && rs.enum_nulls > 0) {
    notes.push(t.enumWerteGeleert(rs.enum_nulls));
  }
  if (typeof rs.coverage_misses === "number" && rs.coverage_misses > 0) {
    notes.push(t.faktenNichtAbgedeckt(rs.coverage_misses));
  }
  if (rs.followup_ran) notes.push(t.nachschlagAusgefuehrt);
  return notes;
}

function ExtractingState({ status }: { status: "queued" | "extracting" }) {
  const t = useT();
  const label =
    status === "queued" ? t.datasets.inWarteschlange : t.datasets.wirdExtrahiert;
  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="bg-warning-soft border border-warning/30 rounded-lg p-4 flex items-center gap-3">
        <span className="w-4 h-4 rounded-full border-2 border-warning border-t-transparent animate-spin shrink-0" />
        <div>
          <div className="text-sm font-medium text-warning">
            {label}
          </div>
          <div className="text-xs text-warning mt-0.5">
            {t.datasets.extraktionHinweis}
          </div>
        </div>
      </div>
      <div className="border border-cream-200 dark:border-ink-600 rounded-lg bg-cream-50 dark:bg-ink-800 p-4 space-y-2.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-4 bg-cream-200/70 dark:bg-ink-700/60 rounded-md animate-pulse"
            style={{ width: `${90 - (i % 3) * 12}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function CreateWizard({ onCreated }: { onCreated: (id: string) => Promise<void> }) {
  const t = useT();
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState<"documents" | "web">("documents");
  const [seedUrls, setSeedUrls] = useState("");
  const [mustContain, setMustContain] = useState("");
  const [docs, setDocs] = useState<Doc[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [columns, setColumns] = useState<DatasetColumn[]>([
    { name: "thema", type: "text", description: "" },
    { name: "aussage", type: "text", description: "" },
  ]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Doc[]>("/documents").then(setDocs).catch(() => setDocs([]));
  }, []);

  const updateCol = (i: number, patch: Partial<DatasetColumn>) =>
    setColumns((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addCol = () =>
    setColumns((cs) => [...cs, { name: "", type: "text", description: "" }]);
  const rmCol = (i: number) => setColumns((cs) => cs.filter((_, idx) => idx !== i));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const source_config =
        sourceType === "documents"
          ? { document_ids: chosen }
          : {
              seed_urls: seedUrls
                .split(/[\s,]+/)
                .map((s) => s.trim())
                .filter(Boolean),
              must_contain_keyword: mustContain.trim() || null,
            };
      const cleaned = columns.filter((c) => c.name.trim() !== "");
      const ds = await api.post<{ id: string }>("/datasets", {
        name,
        source_type: sourceType,
        source_config,
        columns: cleaned,
      });
      await onCreated(ds.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const inferSchema = async () => {
    if (sourceType !== "documents" || chosen.length === 0) {
      setErr(t.datasets.erstDokumenteWaehlen);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const samples: string[] = [];
      for (const id of chosen.slice(0, 3)) {
        const d = await api.get<{ markdown: string }>(`/documents/${id}`);
        samples.push(d.markdown);
      }
      const ds = await api.post<{ id: string }>("/datasets", {
        // Vorgabe-NAME des Datensatzes (wird gespeichert) — Inhalt, keine
        // Beschriftung, deshalb bewusst nicht aus dem Katalog.
        name: name || "Schema-Vorschlag",
        source_type: "documents",
        source_config: { document_ids: chosen },
        columns: [],
      });
      const proposal = await api.post<{ columns: DatasetColumn[] }>(
        `/datasets/${ds.id}/infer-schema`,
        { sample_texts: samples }
      );
      if (proposal.columns?.length) {
        await api.patch(`/datasets/${ds.id}`, { columns: proposal.columns });
      }
      await onCreated(ds.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-6 space-y-4">
      <h2 className="text-lg font-semibold">{t.datasets.neuesDataset}</h2>
      {err && <div className="text-sm text-danger">{err}</div>}

      <label className="block">
        <span className="text-sm font-medium">{t.datasets.name}</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="block w-full border rounded-md px-2 py-1 mt-1"
        />
      </label>

      <div>
        <span className="text-sm font-medium">{t.datasets.quelle}</span>
        <div className="flex gap-3 mt-1 text-sm">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={sourceType === "documents"}
              onChange={() => setSourceType("documents")}
            />
            {t.datasets.dokumenteIngested}
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={sourceType === "web"}
              onChange={() => setSourceType("web")}
            />
            {t.datasets.webDeepResearch}
          </label>
        </div>
      </div>

      {sourceType === "documents" ? (
        <fieldset className="border rounded-md p-3">
          <legend className="text-sm">{t.datasets.dokumenteAuswaehlen}</legend>
          <div className="max-h-48 overflow-y-auto">
            {docs.map((d) => (
              <label key={d.id} className="flex items-center gap-2 text-sm py-0.5">
                <input
                  type="checkbox"
                  checked={chosen.includes(d.id)}
                  onChange={() =>
                    setChosen((s) =>
                      s.includes(d.id) ? s.filter((x) => x !== d.id) : [...s, d.id]
                    )
                  }
                />
                {d.title}
              </label>
            ))}
            {docs.length === 0 && (
              <div className="text-text-2 text-sm">
                {t.datasets.keineDokumenteHochgeladen}
              </div>
            )}
          </div>
        </fieldset>
      ) : (
        <fieldset className="border rounded-md p-3 space-y-2">
          <label className="block text-sm">
            {t.datasets.seedUrls}
            <textarea
              value={seedUrls}
              onChange={(e) => setSeedUrls(e.target.value)}
              rows={3}
              className="block w-full border rounded-md px-2 py-1 mt-1"
            />
          </label>
          <label className="block text-sm">
            {t.datasets.mussEnthalten}
            <input
              value={mustContain}
              onChange={(e) => setMustContain(e.target.value)}
              placeholder={t.datasets.mussEnthaltenPlatzhalter}
              className="block w-full border rounded-md px-2 py-1 mt-1"
            />
          </label>
          <div className="text-xs text-text-2">
            {t.datasets.fakeHinweisVor}
            <code>false</code>
            {t.datasets.fakeHinweisNach}
          </div>
        </fieldset>
      )}

      <fieldset className="border rounded-md p-3">
        <legend className="text-sm flex justify-between items-center w-full">
          <span>{t.datasets.spalten}</span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={inferSchema}
              disabled={busy || sourceType !== "documents" || chosen.length === 0}
              className="text-xs text-accent-700 hover:underline disabled:text-text-3"
            >
              ✨ {t.datasets.schemaVorschlagen}
            </button>
            <button
              type="button"
              onClick={addCol}
              className="text-xs text-accent-700 hover:underline"
            >
              {t.datasets.spalteHinzufuegen}
            </button>
          </span>
        </legend>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-text-2">
              <th className="py-1">{t.datasets.spalteName}</th>
              <th className="py-1">{t.datasets.spalteTyp}</th>
              <th className="py-1">{t.datasets.spalteBeschreibung}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {columns.map((c, i) => (
              <tr key={i}>
                <td className="py-1 pr-2">
                  <input
                    value={c.name}
                    onChange={(e) => updateCol(i, { name: e.target.value })}
                    className="w-full border rounded-md px-2 py-0.5"
                  />
                </td>
                <td className="py-1 pr-2">
                  <select
                    value={c.type}
                    onChange={(e) =>
                      updateCol(i, { type: e.target.value as DatasetColumn["type"] })
                    }
                    className="border rounded-md px-1 py-0.5"
                  >
                    {["text", "number", "date", "bool", "enum", "list"].map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </td>
                <td className="py-1 pr-2">
                  <input
                    value={c.description ?? ""}
                    onChange={(e) => updateCol(i, { description: e.target.value })}
                    className="w-full border rounded-md px-2 py-0.5"
                  />
                </td>
                <td className="py-1 text-xs">
                  <button
                    type="button"
                    onClick={() => rmCol(i)}
                    className="text-danger hover:underline"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </fieldset>

      <Button type="submit" loading={busy}>
        {t.datasets.datasetAnlegen}
      </Button>
    </form>
  );
}

type DetailTab = "table" | "chat";

// Client-seitige Row-Cap (siehe load() unten: api.get(...?limit=5000)).
// Wenn ein Dataset mehr Zeilen hat, sind Filter/Sort nur ueber die ersten
// 5000 Rows valide — Hinweis-Banner macht das transparent.
const CLIENT_ROW_CAP = 5000;

function DatasetDetail({
  datasetId,
  onChange,
  onBack,
}: {
  datasetId: string;
  onChange: () => Promise<void>;
  onBack: () => void;
}) {
  const modal = useModal();
  const t = useT();
  const [ds, setDs] = useState<DatasetOut | null>(null);
  const [rows, setRows] = useState<DatasetRowOut[]>([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchHits, setSearchHits] = useState<DatasetRowOut[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>("table");
  // Client-seitige Sortierung + Inline-Filter pro Spalte. Cap bei
  // CLIENT_ROW_CAP rows (load oben limit=5000) — bei row_count > Cap
  // zeigen wir einen Hinweis dass server-seitiger Filter noch fehlt.
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  // Doc-UUID → Titel: source_ref ist bei Dokument-Datasets die Doc-UUID
  // (runner.py), für die lesbare 'Quelle'-Spalte statt rohem Hash (ST-06).
  const [docTitles, setDocTitles] = useState<Record<string, string>>({});

  // Reset Sort + Filter wenn Dataset wechselt (sonst klebt der State auf
  // einem alten Spaltennamen, der im neuen Dataset gar nicht existiert).
  useEffect(() => {
    setSortCol(null);
    setSortDir("asc");
    setColFilters({});
    setSearchHits([]);
    setSearchQ("");
  }, [datasetId]);

  useEffect(() => {
    setTab("table");
  }, [datasetId]);

  const load = async () => {
    const d = await api.get<DatasetOut>(`/datasets/${datasetId}`);
    setDs(d);
    if (d.status === "ready") {
      setRows(
        await api.get<DatasetRowOut[]>(`/datasets/${datasetId}/rows?limit=5000`)
      );
    } else {
      setRows([]);
    }
    // Dokument-Titel für die 'Quelle'-Spalte (source_ref = Doc-UUID). Nur bei
    // documents-Datasets nötig; Web-Datasets tragen echte URLs.
    if (d.source_type === "documents") {
      try {
        const allDocs = await api.get<Doc[]>("/documents");
        setDocTitles(Object.fromEntries(allDocs.map((x) => [x.id, x.title])));
      } catch {
        /* Fallback: generisches 'Dokument'-Label statt Hash */
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  useEffect(() => {
    if (ds?.status !== "extracting" && ds?.status !== "queued") return;
    const h = setInterval(load, 2500);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ds?.status]);

  const runIt = async () => {
    if (!ds) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/datasets/${datasetId}/run`);
      await load();
      await onChange();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    setBusy(true);
    try {
      const hits = await api.post<DatasetRowOut[]>(
        `/datasets/${datasetId}/search`,
        { query: searchQ, top_k: 10 }
      );
      setSearchHits(hits);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await modal.confirm({
      title: t.datasets.datasetLoeschen,
      description: ds?.name ?? undefined,
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/datasets/${datasetId}`);
    await onChange();
  };

  // Repair-/Lösch-Pfad für Datasets mit verwaister source_config (Mig 0063):
  // deleted (voll verwaist) | requeued (0 Rows) | ok (nichts zu tun).
  const repair = async () => {
    // Repair kann bei voll verwaisten Datasets löschen → vorher bestätigen
    // (konsistent zu del, kein stiller Datenverlust).
    const ok = await modal.confirm({
      title: t.datasets.datasetReparieren,
      description: t.datasets.reparierenBeschreibung,
      confirmLabel: t.datasets.reparieren,
    });
    if (!ok) return;
    const res = await api.post<{ action: string; row_count?: number }>(
      `/datasets/${datasetId}/repair`,
      {},
    );
    const msg =
      res.action === "deleted"
        ? t.datasets.repairGeloescht
        : res.action === "requeued"
          ? t.datasets.repairNeuAufgebaut
          : t.datasets.repairInOrdnung(res.row_count ?? 0);
    await modal.confirm({
      title: t.datasets.reparieren,
      description: msg,
      confirmLabel: t.datasets.ok,
    });
    await onChange();
  };

  // Hooks IMMER vor early-return — sonst React error #310 ("rendered more hooks
  // than during the previous render"), wenn ds erst null und dann gesetzt ist.
  const columnNames = (ds?.columns ?? []).map((c) => c.name);
  const display = searchHits.length > 0 ? searchHits : rows;
  const showSectionCols = hasSectionData(display);
  const processedRows = useMemo(
    () => filterAndSortRows(display, columnNames, colFilters, sortCol, sortDir),
    [display, columnNames, colFilters, sortCol, sortDir],
  );
  const activeFilterCount = Object.values(colFilters).filter(
    (v) => v.trim().length > 0,
  ).length;

  if (!ds) return <div className="p-6 text-ink-50/60">{t.datasets.laedt}</div>;
  const chatDisabled = ds.status !== "ready" || ds.row_count === 0;
  const overCap = ds.row_count > CLIENT_ROW_CAP && searchHits.length === 0;

  const onHeaderClick = (col: string) => {
    if (sortCol === col) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        // Dritter Klick: Sortierung aus
        setSortCol(null);
        setSortDir("asc");
      }
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const exportCsv = () => {
    const filename = csvFilename(ds.name);
    const csv = rowsToCsv(processedRows, columnNames, showSectionCols);
    downloadCsv(filename, csv);
  };

  return (
    <div className="h-full min-w-0 flex flex-col bg-cream-50 dark:bg-ink-900">
      <header className="px-5 py-4 border-b border-cream-200 dark:border-ink-600 flex flex-col gap-3 md:flex-row md:justify-between md:items-start bg-cream-50 dark:bg-ink-900">
        <div className="min-w-0">
          <button
            onClick={onBack}
            className="md:hidden text-xs text-brand-600 dark:text-brand-400 hover:underline inline-flex items-center gap-1 mb-1"
            aria-label={t.datasets.zurueckZurListe}
          >
            <span aria-hidden>←</span> {t.datasets.datasets}
          </button>
          <h2 className="font-display text-2xl text-ink tracking-tight truncate">
            {ds.name}
          </h2>
          <div className="text-xs text-ink-50/60 mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span>
              {ds.source_type === "documents" ? t.datasets.dokumente : t.datasets.web} ·{" "}
              {t.datasets.zeilen(ds.row_count)} · {t.datasets.statusLabel}
            </span>
            <StatusBadge status={ds.status} />
            <CoverageBadge pct={ds.coverage_pct} />
          </div>
          {ds.last_error && (
            <div className="text-sm text-danger mt-1">{ds.last_error}</div>
          )}
          {runStatsNotes(ds.run_stats).length > 0 && (
            <div className="text-xs text-ink-50/50 mt-1">
              {runStatsNotes(ds.run_stats).join(" · ")}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={exportCsv}
            disabled={ds.status !== "ready" || processedRows.length === 0}
            title={t.datasets.csvTitel}
          >
            ↓ {t.datasets.csv}
          </Button>
          <Button
            onClick={runIt}
            loading={ds.status === "extracting" || ds.status === "queued"}
            disabled={
              busy || ds.status === "extracting" || ds.status === "queued"
            }
          >
            {ds.status === "extracting" || ds.status === "queued"
              ? t.datasets.laeuft
              : `▶ ${t.datasets.run}`}
          </Button>
          <Button variant="ghost" size="sm" onClick={repair}>
            {t.datasets.reparierenKlein}
          </Button>
          <Button variant="ghost" size="sm" onClick={del}>
            {t.datasets.loeschenKlein}
          </Button>
        </div>
      </header>

      <div className="flex items-center gap-1 border-b border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-900 px-5">
        {(
          [
            { id: "table", label: t.datasets.tabTabelle },
            { id: "chat", label: t.datasets.tabChat },
          ] as { id: DetailTab; label: string }[]
        ).map((reiter) => (
          <button
            key={reiter.id}
            onClick={() => setTab(reiter.id)}
            disabled={reiter.id === "chat" && chatDisabled}
            className={clsx(
              "relative px-3 py-2 text-sm font-medium transition-colors -mb-px",
              tab === reiter.id
                ? "text-brand-600 dark:text-brand-400 border-b-2 border-brand-500"
                : "text-ink-50/60 hover:text-ink border-b-2 border-transparent",
              reiter.id === "chat" && chatDisabled && "opacity-40 cursor-not-allowed"
            )}
            title={
              reiter.id === "chat" && chatDisabled ? t.datasets.erstRunAusfuehren : undefined
            }
          >
            {reiter.label}
          </button>
        ))}
      </div>

      {tab === "chat" ? (
        <div className="flex-1 min-h-0">
          <DatasetChatView datasetId={datasetId} datasetName={ds.name} />
        </div>
      ) : (
      <div className="flex-1 overflow-hidden flex flex-col p-5 space-y-4">
      {err && <div className="text-sm text-danger">{err}</div>}

      {ds.status === "queued" || ds.status === "extracting" ? (
        <ExtractingState status={ds.status} />
      ) : ds.status === "error" ? (
        <div className="bg-danger-soft border border-danger/30 rounded-lg p-4">
          <div className="text-sm font-medium text-danger mb-1">
            {t.datasets.extraktionFehlgeschlagen}
          </div>
          <div className="text-sm text-danger">
            {ds.last_error ?? t.datasets.unbekannterFehler}
          </div>
        </div>
      ) : (
      <>
      {ds.status === "ready" && (
        <form onSubmit={search} className="flex gap-2">
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder={t.datasets.suchePlatzhalter}
            aria-label={t.datasets.sucheAria}
            className="flex-1 bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-brand-400 placeholder:text-ink-50/40 dark:placeholder:text-cream-200/40"
          />
          <button
            type="submit"
            disabled={busy}
            className="text-sm bg-ink dark:bg-cream-200 dark:text-ink hover:bg-ink-50 text-cream-50 px-3 py-1.5 rounded-md transition-colors"
          >
            🔎
          </button>
          {searchHits.length > 0 && (
            <button
              type="button"
              onClick={() => setSearchHits([])}
              className="text-sm text-ink-50/60 hover:text-ink px-2"
            >
              ×
            </button>
          )}
        </form>
      )}

      {overCap && (
        <div className="text-xs px-3 py-2 rounded-md bg-warning-soft border border-warning/30 text-warning">
          {t.datasets.limitErreicht(
            ds.row_count.toLocaleString("de-DE"),
            CLIENT_ROW_CAP.toLocaleString("de-DE")
          )}
        </div>
      )}

      {ds.status === "ready" && processedRows.length > 0 && columnNames.length >= 3 && (
        <div className="md:hidden text-[11px] text-ink-50/50 -mb-2">
          {t.datasets.horizontalScrollen}
        </div>
      )}
      <div className="flex-1 overflow-auto border border-cream-200 dark:border-ink-600 rounded-lg bg-cream-50 dark:bg-ink-800 shadow-sm">
        <table className="w-full text-sm min-w-[640px] md:min-w-0">
          <thead className="bg-cream-100 dark:bg-ink-700/60 text-xs text-ink-50/70 sticky top-0">
            <tr>
              {showSectionCols && (
                <>
                  <th className="text-left px-3 py-2 font-medium">{t.datasets.section}</th>
                  <th className="text-left px-3 py-2 font-medium">{t.datasets.headingPfad}</th>
                </>
              )}
              {columnNames.map((n) => {
                const isSorted = sortCol === n;
                return (
                  <th key={n} className="text-left px-3 py-2 font-medium">
                    <button
                      type="button"
                      onClick={() => onHeaderClick(n)}
                      className={clsx(
                        "inline-flex items-center gap-1 hover:text-ink transition-colors",
                        isSorted && "text-brand-600 dark:text-brand-400",
                      )}
                      title={t.datasets.sortierungUmschalten}
                    >
                      <span>{n}</span>
                      <span className="text-[10px] opacity-70">
                        {isSorted ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                      </span>
                    </button>
                  </th>
                );
              })}
              <th className="text-left px-3 py-2 font-medium">{t.datasets.quelle}</th>
            </tr>
            <tr className="bg-cream-50 dark:bg-ink-800/80">
              {showSectionCols && (
                <>
                  <th className="px-3 pb-2" />
                  <th className="px-3 pb-2" />
                </>
              )}
              {columnNames.map((n) => (
                <th key={n} className="px-3 pb-2 font-normal">
                  <input
                    type="text"
                    value={colFilters[n] ?? ""}
                    onChange={(e) =>
                      setColFilters((prev) => ({ ...prev, [n]: e.target.value }))
                    }
                    placeholder={t.datasets.filternPlatzhalter}
                    aria-label={t.datasets.spalteFiltern(n)}
                    className="w-full text-xs bg-cream-50 dark:bg-ink-900 border border-cream-200 dark:border-ink-600 rounded-md px-2 py-0.5 focus:outline-none focus:border-brand-400 placeholder:text-ink-50/30 dark:placeholder:text-cream-200/30"
                  />
                </th>
              ))}
              <th className="px-3 pb-2" />
            </tr>
          </thead>
          <tbody>
            {processedRows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-cream-200/70 dark:border-ink-600/60 hover:bg-cream-100/40 dark:hover:bg-ink-700/30 transition-colors"
              >
                {showSectionCols && (
                  <>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      <SectionPill value={r.data?.section_no} />
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-ink-50/70">
                      {formatCell(r.data?.section_path)}
                    </td>
                  </>
                )}
                {columnNames.map((n) => (
                  <td key={n} className="px-3 py-2 align-top text-ink">
                    {formatCell(r.data?.[n])}
                  </td>
                ))}
                <td className="px-3 py-2 align-top text-xs text-ink-50/50">
                  <SourceRef
                    sourceRef={r.source_ref}
                    page={r.data?.table_page}
                    docTitles={docTitles}
                  />
                </td>
              </tr>
            ))}
            {processedRows.length === 0 && (
              <tr>
                <td
                  colSpan={columnNames.length + 1 + (showSectionCols ? 2 : 0)}
                  className="text-center py-8 text-ink-50/60"
                >
                  {ds.status !== "ready"
                    ? t.datasets.statusRunStarten(ds.status)
                    : display.length === 0
                    ? t.datasets.keineZeilen
                    : activeFilterCount > 0
                    ? t.datasets.filterOhneTreffer
                    : t.datasets.keineZeilen}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}
      </div>
      )}
    </div>
  );
}

function formatCell(v: unknown): string {
  // Leere Zellen einfach leer lassen — kein „–" mehr (war traurig anzuschauen).
  if (v == null || v === "") return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// Client-seitige Filter + Sort. Mixed-Types (manche Zellen string, manche
// number) werden konsistent als string verglichen, ausser BEIDE Werte sind
// numerisch parsbar — dann numerisch.
function filterAndSortRows(
  display: DatasetRowOut[],
  columnNames: string[],
  colFilters: Record<string, string>,
  sortCol: string | null,
  sortDir: "asc" | "desc",
): DatasetRowOut[] {
  const activeFilters = Object.entries(colFilters)
    .map(([col, val]) => [col, val.trim().toLowerCase()] as [string, string])
    .filter(([col, val]) => val.length > 0 && columnNames.includes(col));

  let out = display;
  if (activeFilters.length > 0) {
    out = out.filter((r) =>
      activeFilters.every(([col, needle]) => {
        const v = r.data?.[col];
        if (v == null) return false;
        const s = (
          Array.isArray(v)
            ? v.join(", ")
            : typeof v === "object"
            ? JSON.stringify(v)
            : String(v)
        ).toLowerCase();
        return s.includes(needle);
      }),
    );
  }

  if (sortCol && columnNames.includes(sortCol)) {
    const mul = sortDir === "asc" ? 1 : -1;
    out = [...out].sort((a, b) => {
      const av = a.data?.[sortCol];
      const bv = b.data?.[sortCol];
      // null/empty: immer ans Ende (unabhaengig von dir) — sonst springt
      // bei desc die ganze Tabelle ueber leere Zellen.
      const aEmpty = av == null || av === "";
      const bEmpty = bv == null || bv === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      const an = typeof av === "number" ? av : Number(av);
      const bn = typeof bv === "number" ? bv : Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn) && av !== "" && bv !== "") {
        return (an - bn) * mul;
      }
      return String(av).localeCompare(String(bv), "de") * mul;
    });
  }

  return out;
}

// CSV: BOM-prefixed UTF-8 (Excel-DE-kompatibel), Semicolon-Separator (DE-
// Locale), Doppel-Quote-Escape fuer Werte mit ; oder Zeilenumbruch oder ".
function rowsToCsv(
  rows: DatasetRowOut[],
  columnNames: string[],
  includeSectionCols: boolean,
): string {
  const sep = ";";
  const esc = (raw: unknown): string => {
    const s =
      raw == null
        ? ""
        : Array.isArray(raw)
        ? raw.join(", ")
        : typeof raw === "object"
        ? JSON.stringify(raw)
        : String(raw);
    if (
      s.includes(sep) ||
      s.includes('"') ||
      s.includes("\n") ||
      s.includes("\r")
    ) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const headerCells: string[] = [];
  if (includeSectionCols) {
    headerCells.push(esc("section_no"), esc("section_path"));
  }
  for (const n of columnNames) headerCells.push(esc(n));
  headerCells.push(esc("source_ref"));

  const lines: string[] = [headerCells.join(sep)];
  for (const r of rows) {
    const cells: string[] = [];
    if (includeSectionCols) {
      cells.push(esc(r.data?.section_no), esc(r.data?.section_path));
    }
    for (const n of columnNames) cells.push(esc(r.data?.[n]));
    cells.push(esc(r.source_ref));
    lines.push(cells.join(sep));
  }
  return "\ufeff" + lines.join("\r\n");
}

function csvFilename(dsName: string): string {
  const safe = dsName.replace(/[^A-Za-z0-9-_]+/g, "_").slice(0, 60) || "dataset";
  const ts = new Date().toISOString().slice(0, 10);
  return `${safe}_${ts}.csv`;
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Im naechsten Tick freigeben, damit der Browser die URL noch ausgelesen hat.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// 'Quelle'-Zelle: http-URLs bleiben ein Link; Dokument-Quellen (source_ref =
// Doc-UUID, siehe runner.py) zeigen den lesbaren Dokumentnamen (+ Seite, falls
// die Zeile eine table_page trägt) statt des rohen Hash-Prefixes (Katalog
// ST-06). Unbekannte UUID (z. B. Doc gelöscht) → generisches 'Dokument'.
function SourceRef({
  sourceRef,
  page,
  docTitles,
}: {
  sourceRef: string | null;
  page: unknown;
  docTitles: Record<string, string>;
}) {
  const t = useT();
  if (!sourceRef) return <span className="text-ink-50/30">—</span>;
  if (sourceRef.startsWith("http")) {
    return (
      <a
        href={sourceRef}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-600 dark:text-brand-400 hover:underline"
      >
        ↗
      </a>
    );
  }
  const label = docTitles[sourceRef] ?? t.datasets.dokument;
  const pageNo =
    typeof page === "number"
      ? String(page)
      : typeof page === "string" && page.trim()
      ? page.trim()
      : null;
  const full = pageNo ? t.datasets.quelleMitSeite(label, pageNo) : label;
  return (
    <span className="block max-w-[220px] truncate" title={full}>
      {full}
    </span>
  );
}

// section_no als kleine Pill in mono — bessere visuelle Erkennbarkeit als
// reiner Text. Leere Werte werden als „—" subtle angezeigt.
function SectionPill({ value }: { value: unknown }) {
  if (value == null || value === "") {
    return <span className="text-ink-50/30">—</span>;
  }
  const v = String(value);
  return (
    <span className="inline-flex items-center font-mono text-[11px] px-1.5 py-0.5 rounded-md bg-brand-50 dark:bg-brand-700/20 text-brand-700 dark:text-brand-300 border border-brand-100 dark:border-brand-700/40">
      {v}
    </span>
  );
}

// Zeigt die section_no/section_path-Spalten nur, wenn mindestens 1 Row
// einen nicht-leeren Wert hat (Vision-Pfad-Datasets haben sie nie).
function hasSectionData(rows: { data?: Record<string, unknown> }[]): boolean {
  for (const r of rows) {
    const sn = r.data?.section_no;
    const sp = r.data?.section_path;
    if ((sn != null && sn !== "") || (sp != null && sp !== "")) return true;
  }
  return false;
}
